import React, { FC, useCallback } from 'react';
import { TaskCenterQueries, TaskCenterTypes } from '@frontend/api-task-center';
import { Missing } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { PanelHeader } from '@frontend/panel-engine';
import { taskCenterTrackingIds } from '@frontend/task-center-components';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { Button, SpinningLoader, Text } from '@frontend/design-system';
import { headerStyle } from './create-task.styles';

type TaskComponentProps = { selectedTask: TaskCenterTypes.Task | null };
type TaskComponentType = React.ComponentType<TaskComponentProps>;

export const withPrefetchTaskById = (WrappedComponent: TaskComponentType) => {
  const TaskWithPrefetch: FC = () => {
    const { taskId, selectedTask, locationIds, taskMode, setTaskId, setTaskMode, setSelectedTask } = useTaskCenterStore(
      ['taskId', 'locationIds', 'setTaskId', 'setTaskMode', 'taskMode', 'selectedTask', 'setSelectedTask']
    );

    // Determine if we need to fetch task data from the API
    const isEditMode = taskMode === 'edit';
    const shouldFetchTaskFromApi = isEditMode && !!taskId;

    // Fetch task data when in edit mode with a taskId
    const {
      data: fetchedTaskData,
      isLoading: isFetchingTask,
      isFetching: isRefetchingTask,
    } = TaskCenterQueries.useGetTaskById(
      { locationIds, taskId },
      { enabled: shouldFetchTaskFromApi, keepPreviousData: false }
    );

    // Reset task center state and navigate back
    const handleBackNavigation = useCallback(() => {
      setTaskId(null);
      setTaskMode(null);
      setSelectedTask(null);
    }, [setTaskId, setTaskMode, setSelectedTask]);

    // Determine the task to display: fetched from API or selected from store
    const taskToDisplay = shouldFetchTaskFromApi ? fetchedTaskData?.tasks?.[0] ?? null : selectedTask;

    // Show loading/error state when we expect a task but don't have one
    const isLoadingState = isFetchingTask || isRefetchingTask;
    const shouldShowFallbackState = shouldFetchTaskFromApi && !taskToDisplay;

    if (shouldShowFallbackState) {
      return <TaskFallbackState isLoading={isLoadingState} onBackClick={handleBackNavigation} />;
    }

    // Generate unique key to force component remount when task changes
    // This is essential for form components that initialize once on mount
    const componentKey = taskToDisplay?.id || taskToDisplay?.sourceId || 'create-task';

    return <WrappedComponent key={componentKey} selectedTask={taskToDisplay} />;
  };

  return TaskWithPrefetch;
};

interface TaskDetailHeaderProps {
  onBackClick: () => void;
  title: string;
}

interface TaskFallbackStateProps {
  isLoading: boolean;
  onBackClick: () => void;
}

const TaskDetailHeader: FC<TaskDetailHeaderProps> = ({ onBackClick, title }) => {
  const { t } = useTranslation('task-center');

  return (
    <header css={headerStyle}>
      <Button
        iconName='caret-left'
        variant='secondary'
        size='small'
        label={t('Back')}
        onClick={onBackClick}
        trackingId={taskCenterTrackingIds.edit.cancelProgressBackButton}
      />
      <PanelHeader.Title level={3}>{title}</PanelHeader.Title>
    </header>
  );
};

const TaskFallbackState: FC<TaskFallbackStateProps> = ({ isLoading, onBackClick }) => {
  const { t } = useTranslation('task-center');

  return (
    <>
      <TaskDetailHeader onBackClick={onBackClick} title={t('Task Details')} />
      {isLoading ? (
        <SpinningLoader css={{ margin: 'auto' }} />
      ) : (
        <>
          <Missing />
          <Text weight='bold' textAlign='center' size='large'>
            {t('No task to display')}
          </Text>
        </>
      )}
    </>
  );
};
